export default function componentStyleOverrides(theme) {
  const isDark = theme?.customization?.navType === 'dark';
  const radius = theme?.customization?.borderRadius ?? 12;
  const inputBg = isDark ? theme.colors?.darkLevel1 : theme.colors?.grey50;
  const borderColor = isDark ? theme.colors?.darkLevel2 : theme.colors?.grey200;
  const hoverSurface = isDark ? theme.colors?.darkLevel1 : theme.colors?.grey100;

  // soft, layered elevation instead of the flat template default
  const cardShadow = isDark
    ? '0 1px 2px rgba(0,0,0,0.4), 0 6px 20px -8px rgba(0,0,0,0.55)'
    : '0 1px 2px rgba(16,24,40,0.04), 0 8px 24px -12px rgba(16,24,40,0.12)';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: theme.background,
          color: theme.textDark,
          transition: 'background-color .25s ease, color .25s ease',
          scrollbarColor: `${borderColor} transparent`,
          // consumed by inline-styled surfaces (material-table header, date
          // pickers, react-select) so they track the active theme
          '--mt-header-bg': isDark ? theme.colors?.darkLevel2 : theme.colors?.grey100,
          '--mt-header-text': isDark ? theme.darkTextSecondary : theme.colors?.grey700,
          '--mt-header-border': theme.colors?.secondaryMain,
          '--surface': theme.paper,
          '--surface-muted': isDark ? theme.colors?.darkLevel1 : theme.colors?.grey50,
          '--border-subtle': borderColor,
          '--text-primary': theme.textDark,
          // @uiw/react-json-view reads these vars (falling back to light
          // solarized defaults). Override in dark mode so config JSON stays
          // legible on the dark surface.
          ...(isDark
            ? {
                '--w-rjv-color': theme.darkTextPrimary,
                '--w-rjv-key-string': theme.darkTextPrimary,
                '--w-rjv-info-color': theme.darkTextSecondary,
                '--w-rjv-line-color': theme.colors?.darkLevel2,
                '--w-rjv-arrow-color': theme.darkTextSecondary,
                '--w-rjv-brackets-color': theme.darkTextSecondary,
                '--w-rjv-curlybraces-color': theme.darkTextSecondary,
                '--w-rjv-colon-color': theme.darkTextSecondary,
                '--w-rjv-quotes-color': theme.darkTextSecondary,
                '--w-rjv-ellipsis-color': theme.colors?.secondaryMain,
                '--w-rjv-type-string-color': theme.colors?.secondaryMain,
                '--w-rjv-type-int-color': theme.colors?.primaryMain,
                '--w-rjv-type-float-color': theme.colors?.primaryMain,
                '--w-rjv-type-boolean-color': theme.colors?.errorMain,
                '--w-rjv-type-null-color': theme.colors?.errorMain,
                '--w-rjv-type-undefined-color': theme.colors?.errorMain
              }
            : {})
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: borderColor,
          borderRadius: 8,
          border: '2px solid transparent',
          backgroundClip: 'content-box'
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: theme.darkTextSecondary
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 8,
          padding: '7px 16px',
          transition: 'all .18s ease',
          '&:focus-visible': {
            outline: `2px solid ${theme.colors?.secondaryMain}`,
            outlineOffset: 2
          }
        },
        containedPrimary: {
          '&:hover': { boxShadow: cardShadow }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'background-color .18s ease, color .18s ease'
        }
      }
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: 'none', backgroundColor: theme.paper },
        rounded: { borderRadius: `${radius}px` }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: `${radius}px`,
          backgroundColor: theme.paper,
          border: `1px solid ${borderColor}`,
          transition: 'box-shadow .2s ease, border-color .2s ease'
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { color: theme.textDark, padding: '20px 24px' },
        title: { fontSize: '1.05rem', fontWeight: 600 }
      }
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: '24px' } }
    },
    MuiCardActions: {
      styleOverrides: { root: { padding: '20px 24px' } }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: theme.darkTextPrimary,
          borderRadius: 10,
          paddingTop: '10px',
          paddingBottom: '10px',
          transition: 'all .18s ease',
          '&.Mui-selected': {
            color: theme.menuSelected,
            backgroundColor: theme.menuSelectedBack,
            fontWeight: 600,
            '&:hover': { backgroundColor: theme.menuSelectedBack },
            '& .MuiListItemIcon-root': { color: theme.menuSelected }
          },
          '&:hover': {
            backgroundColor: theme.menuSelectedBack,
            color: theme.menuSelected,
            '& .MuiListItemIcon-root': { color: theme.menuSelected }
          }
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { color: theme.darkTextPrimary, minWidth: '36px' }
      }
    },
    MuiListItemText: {
      styleOverrides: { primary: { color: theme.textDark } }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: theme.textDark,
          '&::placeholder': { color: theme.darkTextSecondary, fontSize: '0.875rem' }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: inputBg,
          borderRadius: `${radius}px`,
          transition: 'border-color .18s ease, box-shadow .18s ease',
          '& .MuiOutlinedInput-notchedOutline': { borderColor },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.colors?.secondaryMain
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.colors?.secondaryMain,
            borderWidth: 1,
            boxShadow: `0 0 0 3px ${theme.colors?.secondaryMain}22`
          },
          '&.MuiInputBase-multiline': { padding: 1 }
        },
        input: {
          fontWeight: 500,
          background: inputBg,
          padding: '13.5px 14px',
          borderRadius: `${radius}px`,
          '&.MuiInputBase-inputSizeSmall': {
            padding: '9px 14px',
            '&.MuiInputBase-inputAdornedStart': { paddingLeft: 0 }
          }
        },
        inputAdornedStart: { paddingLeft: 4 },
        notchedOutline: { borderRadius: `${radius}px` }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: { '&.Mui-disabled': { color: theme.colors?.grey300 } },
        mark: { backgroundColor: theme.paper, width: '4px' },
        valueLabel: { color: theme.colors?.secondaryDark }
      }
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: theme.divider, opacity: 1 } }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: theme.colors?.secondaryDark,
          background: theme.colors?.secondaryLight
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          '&.MuiChip-deletable .MuiChip-deleteIcon': { color: 'inherit' }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: theme.divider },
        head: {
          color: theme.darkTextSecondary,
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.72rem',
          letterSpacing: '0.04em'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: hoverSurface } }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: isDark ? theme.colors?.grey900 : theme.paper,
          background: isDark ? theme.colors?.darkTextTitle : theme.colors?.grey700,
          borderRadius: 8,
          fontSize: '0.72rem',
          padding: '6px 10px'
        }
      }
    }
  };
}
